import { useState, useCallback, useMemo } from 'react';
import { OutfitLayer, WardrobeItem, SceneConfiguration, ItemAdjustment } from '../types';
import { POSE_CATEGORIES } from '../constants';
import { CDN_URLS, OCI_FOLDERS } from '../config';

const INITIAL_LAYER: OutfitLayer = {
    garments: [],
    accessories: [],
    appliedGarmentColors: {},
    appliedAccessoryColors: {},
    itemAdjustments: {},
    poseOutputs: {},
    poseDescriptions: {},
    currentPoseInstruction: POSE_CATEGORIES[0].poses[0].instruction,
    sceneConfig: {
        cameraView: 'full-body',
        backgroundPrompt: 'neutral light gray studio backdrop',
        modelPosition: { x: 0, y: 0 },
        modelScale: 1
    }
};

export const useOutfit = () => {
    const [history, setHistory] = useState<OutfitLayer[]>([INITIAL_LAYER]);
    const [historyIndex, setHistoryIndex] = useState(0);
    const [currentOutfitId, setCurrentOutfitId] = useState<string | null>(null);
    const [baseModelImageUrl, setBaseModelImageUrl] = useState<string | null>(`${CDN_URLS.OCI_STORE}${OCI_FOLDERS.CONTENT}Elegant/elegant_blau.png`);

    const currentLayer = useMemo(() => history[historyIndex] || INITIAL_LAYER, [history, historyIndex]);

    const updateLayerWithHistory = useCallback((newLayer: OutfitLayer) => {
        setHistory(prev => {
            const newHistory = prev.slice(0, historyIndex + 1);
            newHistory.push(newLayer);
            if (newHistory.length > 20) newHistory.shift();
            return newHistory;
        });
        setHistoryIndex(prev => Math.min(prev + 1, 19));
    }, [historyIndex]);

    const handleUndo = useCallback(() => {
        if (historyIndex > 0) setHistoryIndex(prev => prev - 1);
    }, [historyIndex]);

    const handleRedo = useCallback(() => {
        if (historyIndex < history.length - 1) setHistoryIndex(prev => prev + 1);
    }, [historyIndex, history.length]);

    const resetOutfit = useCallback(() => {
        setHistory([INITIAL_LAYER]);
        setHistoryIndex(0);
        setBaseModelImageUrl(null);
        setCurrentOutfitId(null);
    }, []);

    return {
        currentLayer,
        updateLayerWithHistory,
        handleUndo,
        handleRedo,
        resetOutfit,
        history,
        historyIndex,
        currentOutfitId,
        setCurrentOutfitId,
        baseModelImageUrl,
        setBaseModelImageUrl
    };
};
